import { Wand2, Loader2, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useOptionalExtraction } from '@/pages/audio-ai/ExtractionContext';

interface Props {
  /** Fecha o drawer mobile depois que dispara. */
  onCloseMobile?: () => void;
  /** Versão compacta (só ícone) na sidebar desktop colapsada. */
  isDesktopCollapsed?: boolean;
}

/**
 * Atalho "Extrair Dados" fixo na sidebar, posicionado logo acima do
 * `SidebarRecordingButton`. Mesmas regras de visibilidade: só fica ativo
 * quando o médico está dentro da ficha de um paciente (`/pacientes/:id`).
 * Fora disso, mostra estado desabilitado.
 *
 * Estado e ação são compartilhados via `ExtractionContext` — o
 * PatientSessionPanel publica `hasAnyInput`/`isExtracting` e instala o
 * handler real; aqui só renderizamos e disparamos.
 */
export const SidebarExtractButton = ({ onCloseMobile, isDesktopCollapsed = false }: Props) => {
  const ctx = useOptionalExtraction();
  const { pathname } = useLocation();

  if (!ctx) return null;

  const { hasAnyInput, isExtracting, trigger, cancel } = ctx;
  const match = pathname.match(/^\/pacientes\/([^/?]+)/);
  const onPatientFile = !!match;
  const enabled = onPatientFile && hasAnyInput && !isExtracting;

  // ── Estado: extraindo — clicável pra INTERROMPER ──────────────────
  if (isExtracting) {
    if (isDesktopCollapsed) {
      return (
        <button
          type="button"
          onClick={cancel}
          className="hidden md:flex mx-2 mb-2 px-1.5 py-2 w-[calc(100%-1rem)] rounded-lg bg-amber/10 border border-amber/30 hover:bg-error/10 hover:border-error/40 group flex-col items-center gap-1.5 transition-colors"
          title="Extraindo dados… clique para interromper"
        >
          <Loader2 size={14} className="text-amber animate-spin group-hover:hidden" />
          <X size={14} className="text-error hidden group-hover:block" />
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={cancel}
        className="px-3 py-2.5 mx-1 mb-2 w-[calc(100%-0.5rem)] rounded-lg bg-amber/10 border border-amber/30 hover:bg-error/10 hover:border-error/40 group flex items-center gap-2 text-left transition-colors"
        title="Interromper a extração em andamento — os inputs do atendimento ficam intactos"
      >
        <Loader2 size={14} className="text-amber animate-spin shrink-0 group-hover:hidden" />
        <X size={14} className="text-error shrink-0 hidden group-hover:block" />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-amber/80 leading-none group-hover:text-error/80">
            Extraindo
          </div>
          <div className="text-sm font-medium text-on-surface leading-tight group-hover:hidden">
            Dados da consulta…
          </div>
          <div className="text-sm font-medium text-error leading-tight hidden group-hover:block">
            Interromper
          </div>
        </div>
      </button>
    );
  }

  // ── Estado: desabilitado (fora da ficha OU sem inputs) ────────────
  if (!enabled) {
    const label = !onPatientFile ? 'Abra paciente' : 'Sem entradas';
    return (
      <button
        type="button"
        disabled
        title={
          !onPatientFile
            ? 'Abra a ficha de um paciente para extrair dados.'
            : 'Adicione áudio, texto, PDF, imagem ou marque um documento manual antes de extrair.'
        }
        className={`mb-2 rounded-lg flex items-center gap-3 text-on-surface-variant/50 border border-dashed border-outline-variant/20 cursor-not-allowed ${
          isDesktopCollapsed
            ? 'md:mx-2 md:w-[calc(100%-1rem)] md:justify-center md:px-0 md:py-2 mx-1 w-[calc(100%-0.5rem)] px-3 py-2.5'
            : 'mx-1 w-[calc(100%-0.5rem)] px-3 py-2.5'
        }`}
      >
        <Wand2 size={16} className="shrink-0" />
        <span
          className={`text-sm flex-1 text-left truncate ${isDesktopCollapsed ? 'md:hidden' : ''}`}
        >
          Extrair Dados
        </span>
        <span
          className={`text-[10px] uppercase tracking-wider text-on-surface-variant/40 shrink-0 ${isDesktopCollapsed ? 'md:hidden' : ''}`}
        >
          {label}
        </span>
      </button>
    );
  }

  // ── Estado: habilitado ────────────────────────────────────────────
  return (
    <button
      type="button"
      onClick={() => {
        trigger();
        onCloseMobile?.();
      }}
      title="Extrair dados desta consulta com IA"
      className={`mb-2 rounded-lg flex items-center gap-3 bg-amber/15 hover:bg-amber/25 text-amber border border-amber/40 ring-1 ring-amber/30 shadow-sm transition-colors font-semibold ${
        isDesktopCollapsed
          ? 'md:mx-2 md:w-[calc(100%-1rem)] md:justify-center md:px-0 md:py-2 mx-1 w-[calc(100%-0.5rem)] px-3 py-2.5'
          : 'mx-1 w-[calc(100%-0.5rem)] px-3 py-2.5'
      }`}
    >
      <Wand2 size={16} className="shrink-0" />
      <span className={`text-sm flex-1 text-left ${isDesktopCollapsed ? 'md:hidden' : ''}`}>
        Extrair Dados
      </span>
    </button>
  );
};
